import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import EsimCard from "../EsimCard/EsimCard";
import LinkButton from "../../Elements/LinkButton/LinkButton";
import { markEsimAsDeleted, markEsimAsUndeleted } from "../../../services/EsimService";

import arrowRight from "../../../assets/icons/navigation/arrow-right-primary.png";
import "./EsimsHorizontalScroll.css";

const EsimsHorizontalScroll = ({ esims, selectedEsim: propSelectedEsim, onSelectedEsimChange, filter, onRefresh }) => {
  const navigate = useNavigate();
  const [localSelectedEsim, setLocalSelectedEsim] = useState(null);
  const scrollRef = useRef(null);
  const cardWidth = 380;

  const selectedEsim = propSelectedEsim || localSelectedEsim;

  const sortedEsims = useMemo(() => {
    if (!esims) return [];
    return [...esims].sort((a, b) => {
      if (a.installed && !b.installed) return -1;
      if (!a.installed && b.installed) return 1;
      return 0;
    });
  }, [esims]);

  useEffect(() => {
    if (propSelectedEsim) {
      setLocalSelectedEsim(propSelectedEsim);
    } else if (sortedEsims.length > 0 && !selectedEsim) {
      setLocalSelectedEsim(sortedEsims[0]);
      onSelectedEsimChange?.(sortedEsims[0]);
    }
  }, [propSelectedEsim, sortedEsims, selectedEsim]);

  useEffect(() => {
    if (propSelectedEsim) {
      setLocalSelectedEsim(propSelectedEsim);
    }
  }, [propSelectedEsim]);

  const handleEsimSelect = (clickedEsim) => {
    setLocalSelectedEsim(clickedEsim);
    onSelectedEsimChange?.(clickedEsim);
  };

  const scrollNext = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: cardWidth, behavior: "smooth" });
    }
  };

  const canScrollRight = useCallback(() => {
    if (!scrollRef.current || sortedEsims.length <= 1) return false;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    return scrollLeft + clientWidth < scrollWidth - 20;
  }, [sortedEsims.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      container.style.scrollSnapType = "";
      container.style.scrollSnapType = "";
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const [canScroll, setCanScroll] = useState(true);

  useEffect(() => {
    setCanScroll(canScrollRight());
  }, [canScrollRight]);

  const handleScrollUpdate = useCallback(() => {
    setCanScroll(canScrollRight());
  }, [canScrollRight]);

  const handleBuyEsim = () => {
    navigate("/shop");
  };

  const handleDeletePress = async (esim) => {
    try {
      await markEsimAsDeleted([esim.iccid]);
      onRefresh?.();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleRestorePress = async (esim) => {
    try {
      await markEsimAsUndeleted([esim.iccid]);
      onRefresh?.();
    } catch (err) {
      console.error("Restore failed:", err);
    }
  };

  if (!sortedEsims || sortedEsims.length === 0) {
    const emptyMsg =
      filter === "archived" ? "No archived eSIMs" : filter === "deleted" ? "No deleted eSIMs" : "No eSIMs yet";

    return (
      <div className="esims-horizontal-scroll">
        <div className="empty-state">
          <p>{emptyMsg}</p>
          {filter === "current" && <LinkButton onClick={() => navigate("/shop")}>Buy your first eSIM</LinkButton>}
        </div>
      </div>
    );
  }

  return (
    <div className="esims-horizontal-scroll">
      <div className="scroll-wrapper">
        <div className="scroll-container" ref={scrollRef} onScroll={handleScrollUpdate}>
          <div className="cards-wrapper">
            {sortedEsims.map((esim) => (
              <EsimCard
                key={esim.iccid}
                esim={esim}
                selected={selectedEsim?.iccid === esim.iccid}
                onToggle={handleEsimSelect}
                isDeleted={filter === "deleted"}
                onDeletePress={handleDeletePress}
                onRestorePress={handleRestorePress}
              />
            ))}
          </div>
        </div>
        <button
          className={`scroll-next-btn ${!canScrollRight() ? "scroll-next-btn--disabled" : ""}`}
          onClick={scrollNext}
          disabled={!canScrollRight()}
        >
          <img src={arrowRight} alt="Next" />
        </button>
      </div>
    </div>
  );
};

export default EsimsHorizontalScroll;
